// frontend/src/design-system/components/ImageUploadInput.tsx
//
// Reusable image input used across super-admin (blog/reviews/seo/company)
// and tenant settings (workspace logo / invoice branding). Two modes:
//
//   - Upload — drag-and-drop or click-to-browse, posts multipart to the
//     selected endpoint and surfaces upload progress via XMLHttpRequest
//     (fetch can't report upload progress).
//   - URL    — paste an existing https URL.
//
// Either path produces a string URL that is returned to the parent via
// `onChange`. The component is controlled: it never owns the URL — it just
// owns the local UI state (mode, progress, error, drag-over).
//
// Auth: the JWT lives under `auth_token` in localStorage. We reuse the
// project's `getToken()` helper rather than reading the key directly so we
// stay aligned with the rest of the frontend (api-client.ts).

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, Link as LinkIcon, Upload, X, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getToken } from '../../core/api-client';

export interface ImageUploadInputProps {
  /** Current image URL value (controlled). */
  value: string;
  /** Called with the new URL whenever the user uploads a new file or types a new URL. */
  onChange: (url: string) => void;
  /** Which endpoint to hit. */
  endpoint: 'super-admin' | 'tenant';
  /** Required when endpoint = 'super-admin' — sent as the form field "area". */
  area?: 'blog' | 'reviews' | 'seo' | 'company';
  /** Max upload size in bytes; default 5 * 1024 * 1024. */
  maxBytes?: number;
  /** Optional label rendered above the component. */
  label?: string;
  /** Optional helper/description text rendered below the label. */
  helperText?: string;
  /** Optional className applied to the root. */
  className?: string;
  /** Disables both modes (drop zone, browse, URL input). Server still enforces auth/perm. */
  disabled?: boolean;
}

type Mode = 'upload' | 'url';

interface UploadResponse {
  url: string;
  contentType?: string;
  size?: number;
}

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

export function ImageUploadInput({
  value,
  onChange,
  endpoint,
  area,
  maxBytes = DEFAULT_MAX_BYTES,
  label,
  helperText,
  className,
  disabled = false,
}: ImageUploadInputProps) {
  const [mode, setMode] = React.useState<Mode>('upload');
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const xhrRef = React.useRef<XMLHttpRequest | null>(null);

  // Abort any in-flight upload if the component unmounts.
  React.useEffect(() => () => {
    if (xhrRef.current) xhrRef.current.abort();
  }, []);

  const maxBytesLabel = React.useMemo(() => {
    const mb = maxBytes / 1024 / 1024;
    return mb >= 1 ? `${Math.round(mb)} MB` : `${Math.round(maxBytes / 1024)} KB`;
  }, [maxBytes]);

  const endpointUrl = endpoint === 'super-admin'
    ? '/api/super-admin/uploads/image'
    : '/api/tenant/uploads/image';

  const startUpload = React.useCallback((file: File) => {
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file');
      return;
    }
    if (file.size > maxBytes) {
      setError(`File exceeds ${maxBytesLabel}`);
      return;
    }

    const form = new FormData();
    form.append('file', file);
    if (endpoint === 'super-admin') {
      form.append('area', area ?? 'blog');
    }

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    setUploading(true);
    setProgress(0);

    xhr.open('POST', endpointUrl, true);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) {
        setProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    };

    xhr.onload = () => {
      xhrRef.current = null;
      setUploading(false);
      const raw = xhr.responseText || '';
      let body: unknown = null;
      if (raw) {
        try { body = JSON.parse(raw); } catch { /* leave null */ }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const resp = body as UploadResponse | null;
        if (resp && typeof resp.url === 'string' && resp.url) {
          setProgress(100);
          setError(null);
          onChange(resp.url);
        } else {
          setError('Upload failed');
        }
        return;
      }
      // Error path — surface server-provided message.
      const err = (body as { error?: { message?: string }; message?: string } | null) ?? null;
      const msg = err?.error?.message ?? err?.message ?? 'Upload failed';
      setError(msg);
      setProgress(0);
    };

    xhr.onerror = () => {
      xhrRef.current = null;
      setUploading(false);
      setProgress(0);
      setError('Network error during upload');
    };

    xhr.onabort = () => {
      xhrRef.current = null;
      setUploading(false);
      setProgress(0);
    };

    xhr.send(form);
  }, [area, endpoint, endpointUrl, maxBytes, maxBytesLabel, onChange]);

  const onBrowseClick = () => {
    if (uploading || disabled) return;
    fileInputRef.current?.click();
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) startUpload(file);
    // Reset so picking the same file twice still fires `change`.
    e.target.value = '';
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (uploading || disabled) return;
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading || disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  };

  const clearValue = () => {
    setError(null);
    onChange('');
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <label className="text-[12px] font-semibold tracking-[0.04em] text-ink/80 dark:text-white/80">
          {label}
        </label>
      )}
      {helperText && (
        <p className="text-[12.5px] text-ink/55 dark:text-white/55 leading-relaxed -mt-0.5">
          {helperText}
        </p>
      )}

      {/* Mode toggle */}
      <div
        role="tablist"
        aria-label="Image source"
        className="inline-flex items-center gap-1 rounded-full bg-ink/5 dark:bg-white/[0.06] p-1 ring-1 ring-ink/10 dark:ring-white/10 self-start mt-1"
      >
        <ModeTab
          active={mode === 'upload'}
          onClick={() => { setMode('upload'); setError(null); }}
          icon={<Upload className="h-3.5 w-3.5" />}
          label="Upload"
        />
        <ModeTab
          active={mode === 'url'}
          onClick={() => { setMode('url'); setError(null); }}
          icon={<LinkIcon className="h-3.5 w-3.5" />}
          label="URL"
        />
      </div>

      {/* Preview + controls grid */}
      <div className="grid grid-cols-1 sm:grid-cols-[180px_minmax(0,1fr)] gap-3 mt-1">
        {/* Thumbnail */}
        <div className="relative rounded-2xl bg-white/60 dark:bg-white/[0.04] ring-1 ring-ink/10 dark:ring-white/10 overflow-hidden flex items-center justify-center h-[140px] sm:h-[160px]">
          {value ? (
            <>
              <img
                src={value}
                alt="Selected"
                className="max-h-[240px] w-full h-full object-contain"
                onError={() => setError('Could not load image at that URL')}
              />
              <button
                type="button"
                onClick={clearValue}
                disabled={uploading}
                aria-label="Clear image"
                className="absolute top-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink/70 text-white shadow-sm hover:bg-ink disabled:opacity-50 transition"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-1.5 text-ink/35 dark:text-white/35">
              <ImageIcon className="h-6 w-6" />
              <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold">No image</span>
            </div>
          )}
        </div>

        {/* Right column: upload zone OR URL input */}
        <div className="min-w-0">
          <AnimatePresence mode="wait" initial={false}>
            {mode === 'upload' ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                <div
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onClick={onBrowseClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onBrowseClick();
                    }
                  }}
                  className={cn(
                    'group relative flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed cursor-pointer transition-all px-5 py-7 text-center',
                    'bg-white/55 dark:bg-white/[0.04]',
                    dragOver
                      ? 'border-coral/60 bg-coral/[0.06] ring-2 ring-coral/30'
                      : 'border-ink/15 dark:border-white/15 hover:border-ink/30 hover:bg-white/70',
                    uploading && 'pointer-events-none opacity-80',
                    disabled && 'pointer-events-none opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-coral/12 text-coral-700 ring-1 ring-coral/20">
                    <Upload className="h-4 w-4" />
                  </div>
                  <div className="text-[13px] text-ink/80 dark:text-white/80">
                    <span className="font-semibold">Drop an image</span>
                    <span className="text-ink/55 dark:text-white/55"> or click to browse</span>
                  </div>
                  <p className="text-[11px] text-ink/45 dark:text-white/45">
                    PNG, JPG, GIF, WebP, SVG, AVIF up to {maxBytesLabel}
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onFileInputChange}
                  />
                </div>

                {/* Progress bar */}
                {uploading && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-ink/55 dark:text-white/55">
                        Uploading
                      </span>
                      <span className="text-[11px] tabular-nums text-ink/65 dark:text-white/65">
                        {progress}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink/8 dark:bg-white/10">
                      <motion.div
                        className="h-full bg-coral"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ type: 'spring', stiffness: 160, damping: 26 }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="url"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col gap-2"
              >
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink/40" />
                  <input
                    type="url"
                    value={value}
                    placeholder="https://example.com/image.png"
                    disabled={disabled}
                    onChange={(e) => { setError(null); onChange(e.target.value); }}
                    className={cn(
                      'w-full rounded-xl bg-white/70 dark:bg-white/[0.05] ring-1 ring-ink/10 dark:ring-white/10',
                      'px-9 py-2.5 text-[13.5px] text-ink dark:text-white placeholder:text-ink/35',
                      'focus:outline-none focus:ring-2 focus:ring-coral/50 transition',
                      disabled && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                </div>
                <p className="text-[11px] text-ink/45 dark:text-white/45">
                  Paste a direct image URL. We don't re-host pasted URLs — they're stored as-is.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Inline error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-rose-500/10 px-2.5 py-1.5 text-[12px] text-rose-700 ring-1 ring-rose-500/20"
            >
              <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
              <span className="leading-snug">{error}</span>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ModeTabProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function ModeTab({ active, onClick, icon, label }: ModeTabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] transition',
        active
          ? 'bg-white dark:bg-white/[0.12] text-ink dark:text-white shadow-sm ring-1 ring-ink/10 dark:ring-white/15'
          : 'text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
